import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";

import DataTablePagination from "../../../components/common/datatable/DataTablePagination";
import ButtonComponent from "../../../components/common/button/Button";

const PriceMemoData = (props) => {
  const [disableFilterBtn, setDisableFilterBtn] = useState(true);
  const priceMemoReportData = useSelector(
    (state) => state?.fetchPriceMemoReports
  );
  const [filteredTotalItems, setFilteredTotalItems] = useState(priceMemoReportData?.data?.totalNumberOfItems);
  const [filteredTotalColumn, setFilteredTotalColumn] = useState(0);
  const [isGridReady, setIsGridReady] = useState(false);
  const [columnState, setColumnState] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState('');

  const handlePostFilterChanged = (filteredCount, totalCount) => {
    setFilteredTotalItems(filteredCount); // Update the filtered total items
  };

  const renderExpandableText = (params, maxLength = 50) => {
    const text = params.value || ""; // The error details text
    const isLongText = text.length > maxLength;

    // Initialize the showFullText property for the row if not already set
    if (!params.node.showFullText) {
      params.node.showFullText = false;
    }

    const toggleText = () => {
      params.node.showFullText = !params.node.showFullText;
      params.api.refreshCells({ rowNodes: [params.node], force: true }); // Refresh the cell to update the UI
    };

    return isLongText ? (
      <div>
        {params.node.showFullText ? (
          <>
            <span>{text}</span>{" "}
            <button
              onClick={toggleText}
              style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              Less
            </button>
          </>
        ) : (
          <>
            <span>{text.substring(0, maxLength)}...</span>{" "}
            <button
              onClick={toggleText}
              style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              More
            </button>
          </>
        )}
      </div>
    ) : (
      <span>{text}</span>
    );
  };

  const columnData = useMemo(() => {
    if(props?.data?.priceMemoType === 'SIS') {
      return [
        {
          headerName: 'Year',
          field: "yearSIS",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('yearSIS')
          }
        },
        {
          headerName: 'Price Tag ID',
          field: "priceTagId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('priceTagId')
          }
        },
        {
          headerName: "User Id",
          field: "userId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: "User Name",
          field: "userName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Division',
          field: "division",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Change Date',
          field: "changeDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('changeDate')
          },
          // return a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.changeDate;
            if (!v) return null;
            const s = typeof v === 'string' ? v.replace(/\.00$/, '') : v;
            const d = new Date(s);
            return isNaN(d.getTime()) ? null : d;
          },

          // display as DD-MM-YYYY (prefer original string when available)
          valueFormatter: (params) => {
            const src = params.data?.changeDate;
            if (src && typeof src === 'string') {
              const s = src.replace(/\.00$/, '');
              const d = new Date(s);
              if (!isNaN(d.getTime())) {
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${dd}-${mm}-${yyyy}`;
              }
              return src;
            }
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          filterParams: {
            comparator: (filterLocalDateAtMidnight, cellValue, node) => {
              const src = node?.data?.changeDate ?? cellValue;
              if (!src) return -1;
              const s = typeof src === 'string' ? src.replace(/\.00$/, '') : src;
              const d = new Date(s);
              if (isNaN(d.getTime())) return -1;
              const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
              const filterYMD = new Date(
                filterLocalDateAtMidnight.getFullYear(),
                filterLocalDateAtMidnight.getMonth(),
                filterLocalDateAtMidnight.getDate()
              );
              if (cellYMD.getTime() === filterYMD.getTime()) return 0;
              return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
            },
            browserDatePicker: true,
          },

          // ensure sort parses the original string (works even when valueGetter removed elsewhere)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.changeDate ?? valueA;
            const bStr = nodeB?.data?.changeDate ?? valueB;
            const aDate = aStr ? new Date((typeof aStr === 'string' ? aStr.replace(/\.00$/, '') : aStr)) : null;
            const bDate = bStr ? new Date((typeof bStr === 'string' ? bStr.replace(/\.00$/, '') : bStr)) : null;
            const ta = aDate && !isNaN(aDate.getTime()) ? aDate.getTime() : null;
            const tb = bDate && !isNaN(bDate.getTime()) ? bDate.getTime() : null;
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'Resolved Date',
          field: "resolvedDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('resolvedDate')
          },
          // provide a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.resolvedDate;
            if (!v || typeof v !== 'string') return null;
            const parts = v.split('-'); // expecting "DD-MM-YYYY"
            if (parts.length !== 3) return null;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
          },

          // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
          valueFormatter: (params) => {
            const src = params.data?.resolvedDate;
            if (src && typeof src === 'string') return src;
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          filterParams: {
            comparator: (filterLocalDateAtMidnight, cellValue, node) => {
              const src = node?.data?.resolvedDate ?? cellValue;
              if (!src) return -1;
              // parse "DD-MM-YYYY"
              const parts = typeof src === 'string' ? src.split('-') : null;
              if (!parts || parts.length !== 3) return -1;
              const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
              if (isNaN(d.getTime())) return -1;
              const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
              const filterYMD = new Date(
                filterLocalDateAtMidnight.getFullYear(),
                filterLocalDateAtMidnight.getMonth(),
                filterLocalDateAtMidnight.getDate()
              );
              if (cellYMD.getTime() === filterYMD.getTime()) return 0;
              return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
            },
            browserDatePicker: true,
          },

          // sort by parsing the source string from the row data (robust even if other getters exist)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.resolvedDate ?? valueA;
            const bStr = nodeB?.data?.resolvedDate ?? valueB;
            const parse = (s) => {
              if (!s || typeof s !== 'string') return null;
              const p = s.split('-');
              if (p.length !== 3) return null;
              const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
              return isNaN(d.getTime()) ? null : d.getTime();
            };
            const ta = parse(aStr);
            const tb = parse(bStr);
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'Resolved Time',
          field: "resolvedTime",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('resolvedTime')
          }
        },
        {
          headerName: 'Plant Id',
          field: "plantId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('plantId')
          }
        },
        {
          headerName: 'Plant Name',
          field: "plantName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('plantName')
          }
        },
        {
          headerName: 'Supplier Id',
          field: "supplierId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierId')
          }
        },
        {
          headerName: 'Sap Vendor Number',
          field: "sapSupplierId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Sap Vendor Description',
          field: "sapSupplierDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
        },
        {
          headerName: 'Supplier',
          field: "supplierName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierName')
          }
        },
        {
          headerName: 'Location Name',
          field: "locationName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('locationName')
          }
        },
        {
          headerName: 'Supplier Country',
          field: "facilitycountry",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'PIN',
          field: "pin",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('pin')
          }
        },
        {
          headerName: 'Sap Material Number',
          field: "sapMaterialNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('sapMaterialNumber')
          }
        },
        {
          headerName: 'Sap Material Description',
          field: "sapMaterialDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
        },
        {
          headerName: 'Material Group',
          field: "extMaterialGroup",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('extMaterialGroup')
          }
        },
        {
          headerName: 'Ingredient',
          field: "ingredient",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('ingredient')
          }
        },
        {
          headerName: 'Package Name',
          field: "packageName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('packageName')
          }
        },
        {
          headerName: 'Package Size',
          field: "packSize",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('packSize')
          }
        },
        {
          headerName: 'Bulk Flag',
          field: "isbulk",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'UOM',
          field: "uom",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('uom')
          }
        },
        {
          headerName: 'Price per Kg',
          field: "pricePerKG",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('pricePerKG')
          }
        },
        {
          headerName: 'Currency',
          field: "currency",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('currency')
          }
        },
        {
          headerName: 'Effective From',
          field: "effectiveStartDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('effectiveStartDate')
          },
          // provide a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.effectiveStartDate;
            if (!v || typeof v !== 'string') return null;
            const parts = v.split('-'); // expecting "DD-MM-YYYY"
            if (parts.length !== 3) return null;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
          },

          // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
          valueFormatter: (params) => {
            const src = params.data?.effectiveStartDate;
            if (src && typeof src === 'string') return src;
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          filterParams: {
            comparator: (filterLocalDateAtMidnight, cellValue, node) => {
              const src = node?.data?.effectiveStartDate ?? cellValue;
              if (!src) return -1;
              // parse "DD-MM-YYYY"
              const parts = typeof src === 'string' ? src.split('-') : null;
              if (!parts || parts.length !== 3) return -1;
              const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
              if (isNaN(d.getTime())) return -1;
              const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
              const filterYMD = new Date(
                filterLocalDateAtMidnight.getFullYear(),
                filterLocalDateAtMidnight.getMonth(),
                filterLocalDateAtMidnight.getDate()
              );
              if (cellYMD.getTime() === filterYMD.getTime()) return 0;
              return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
            },
            browserDatePicker: true,
          },

          // sort by parsing the source string from the row data (robust even if other getters exist)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.effectiveStartDate ?? valueA;
            const bStr = nodeB?.data?.effectiveStartDate ?? valueB;
            const parse = (s) => {
              if (!s || typeof s !== 'string') return null;
              const p = s.split('-');
              if (p.length !== 3) return null;
              const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
              return isNaN(d.getTime()) ? null : d.getTime();
            };
            const ta = parse(aStr);
            const tb = parse(bStr);
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'Effective To',
          field: "effectiveEndDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('effectiveEndDate')
          },
          // provide a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.effectiveEndDate;
            if (!v || typeof v !== 'string') return null;
            const parts = v.split('-'); // expecting "DD-MM-YYYY"
            if (parts.length !== 3) return null;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
          },

          // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
          valueFormatter: (params) => {
            const src = params.data?.effectiveEndDate;
            if (src && typeof src === 'string') return src;
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          filterParams: {
            comparator: (filterLocalDateAtMidnight, cellValue, node) => {
              const src = node?.data?.effectiveEndDate ?? cellValue;
              if (!src) return -1;
              // parse "DD-MM-YYYY"
              const parts = typeof src === 'string' ? src.split('-') : null;
              if (!parts || parts.length !== 3) return -1;
              const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
              if (isNaN(d.getTime())) return -1;
              const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
              const filterYMD = new Date(
                filterLocalDateAtMidnight.getFullYear(),
                filterLocalDateAtMidnight.getMonth(),
                filterLocalDateAtMidnight.getDate()
              );
              if (cellYMD.getTime() === filterYMD.getTime()) return 0;
              return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
            },
            browserDatePicker: true,
          },

          // sort by parsing the source string from the row data (robust even if other getters exist)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.effectiveEndDate ?? valueA;
            const bStr = nodeB?.data?.effectiveEndDate ?? valueB;
            const parse = (s) => {
              if (!s || typeof s !== 'string') return null;
              const p = s.split('-');
              if (p.length !== 3) return null;
              const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
              return isNaN(d.getTime()) ? null : d.getTime();
            };
            const ta = parse(aStr);
            const tb = parse(bStr);
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'Allocation Quantity',
          field: "allocationQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('allocationQty')
          },
        },
        {
          headerName: 'Allocation Notes',
          field: "allocationNotes",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('allocationNotes')
          },
          cellRenderer: (params) => renderExpandableText(params, 50)
        },
        {
          headerName: 'From (Qty)',
          field: "fromQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('fromQty')
          }
        },
        {
          headerName: 'To (Qty)',
          field: "toQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('toQty')
          }
        },
        {
          headerName: 'MOQ Comments',
          field: "moqComments",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('moqComments')
          },
          cellRenderer: (params) => renderExpandableText(params, 50)
        },
        {
          headerName: 'Payment Term',
          field: "paymentTerms",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('paymentTerms')
          },
          cellRenderer: (params) => renderExpandableText(params, 50)
        },
        {
          headerName: 'Manufacturing Lead Time',
          field: "manufacturingLeadTime",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('manufacturingLeadTime')
          }
        },
        {
          headerName: 'Auto Co Ship',
          field: "autoCoship",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('autoCoship')
          }
        },
        {
          headerName: 'Inco Terms',
          field: "incoterms",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('incoterms')
          }
        },
        {
          headerName: 'Named Place',
          field: "namedPlace",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('namedPlace')
          }
        },
        {
          headerName: 'Supply Point Number',
          field: "supplypointid",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Supply Point Description',
          field: "supplypointname",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
        },
        {
          headerName: 'Manager Initials',
          field: "managerInitials",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('managerInitials')
          }
        },
        {
          headerName: 'Manager',
          field: "managerName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('managerName')
          }
        },
        {
          headerName: 'PC Initial',
          field: "pcInitial",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('pcInitial')
          }
        },
        {
          headerName: 'SCA',
          field: "sca",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('sca')
          }
        },
        {
          headerName: 'PMU Date',
          field: "pmUdate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('pmUdate')
          }
        },
        {
          headerName: 'Price Per Lb',
          field: "pricePerLb",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('pricePerLb')
          }
        },
        {
          headerName: 'Manufacturing Facility Number',
          field: "facilitynum",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Manufacturing Facility Description',
          field: "facilityname",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
        },
        {
          headerName: 'S-Code',
          field: "specequivalentcode",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: "Created Date",
          field: "lastUpdateDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          // provide a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.lastUpdateDate;
            if (!v || typeof v !== 'string') return null;
            const parts = v.split('-'); // expecting "DD-MM-YYYY"
            if (parts.length !== 3) return null;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
          },

          // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
          valueFormatter: (params) => {
            const src = params.data?.lastUpdateDate;
            if (src && typeof src === 'string') return src;
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          filterParams: {
            comparator: (filterLocalDateAtMidnight, cellValue, node) => {
              const src = node?.data?.lastUpdateDate ?? cellValue;
              if (!src) return -1;
              // parse "DD-MM-YYYY"
              const parts = typeof src === 'string' ? src.split('-') : null;
              if (!parts || parts.length !== 3) return -1;
              const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
              if (isNaN(d.getTime())) return -1;
              const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
              const filterYMD = new Date(
                filterLocalDateAtMidnight.getFullYear(),
                filterLocalDateAtMidnight.getMonth(),
                filterLocalDateAtMidnight.getDate()
              );
              if (cellYMD.getTime() === filterYMD.getTime()) return 0;
              return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
            },
            browserDatePicker: true,
          },

          // sort by parsing the source string from the row data (robust even if other getters exist)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.lastUpdateDate ?? valueA;
            const bStr = nodeB?.data?.lastUpdateDate ?? valueB;
            const parse = (s) => {
              if (!s || typeof s !== 'string') return null;
              const p = s.split('-');
              if (p.length !== 3) return null;
              const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
              return isNaN(d.getTime()) ? null : d.getTime();
            };
            const ta = parse(aStr);
            const tb = parse(bStr);
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: "Created Time",
          field: "lastUpdateTime",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Open PO',
          field: "openPO",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('openPO')
          }
        },
        {
          headerName: "Error Details",
          field: "errorDetails",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50)
        },
        // {
        //   headerName: "Religious Attributes",
        //   headerClass: "center-header",
        //   children: [
        //     {
        //       headerName: 'Halal',
        //       field: "halalstatus",
        //       sortable: true,
        //       resizable: false,
        //       filterParams: commonFilterParams,
        //       filter: commonFilter,
        //       suppressMovable: true,
        //       unSortIcon: true,
        //     },
        //     {
        //       headerName: 'Kosher PO',
        //       field: "kosherPO",
        //       sortable: true,
        //       resizable: false,
        //       filterParams: commonFilterParams,
        //       filter: commonFilter,
        //       suppressMovable: true,
        //       unSortIcon: true,
        //     },
        //     {
        //       headerName: 'Kosher Yr',
        //       field: "kosherYr",
        //       sortable: true,
        //       resizable: false,
        //       filterParams: commonFilterParams,
        //       filter: commonFilter,
        //       suppressMovable: true,
        //       unSortIcon: true,
        //     },
        //     {
        //       headerName: 'Kosher',
        //       field: "kosherstatus",
        //       sortable: true,
        //       resizable: false,
        //       filterParams: commonFilterParams,
        //       filter: commonFilter,
        //       suppressMovable: true,
        //       unSortIcon: true,
        //     },
        //     {
        //       headerName: 'Kosher Passover',
        //       field: "kosherkfpner",
        //       sortable: true,
        //       resizable: false,
        //       filterParams: commonFilterParams,
        //       filter: commonFilter,
        //       suppressMovable: true,
        //       unSortIcon: true,
        //     },
        //     {
        //       headerName: 'Kosher Passover (OU)',
        //       field: "kosherkfpou",
        //       sortable: true,
        //       resizable: false,
        //       filterParams: commonFilterParams,
        //       filter: commonFilter,
        //       suppressMovable: true,
        //       unSortIcon: true,
        //     },
        //     {
        //       headerName: 'Kosher Year (NER)',
        //       field: "kosherkyrner",
        //       sortable: true,
        //       resizable: false,
        //       filterParams: commonFilterParams,
        //       filter: commonFilter,
        //       suppressMovable: true,
        //       unSortIcon: true,
        //     },
        //     {
        //       headerName: 'Kosher Year (OU)',
        //       field: "kosherkyrou",
        //       sortable: true,
        //       resizable: false,
        //       filterParams: commonFilterParams,
        //       filter: commonFilter,
        //       suppressMovable: true,
        //       unSortIcon: true,
        //     },
        //     {
        //       headerName: 'Allergen',
        //       field: "allergenstatus",
        //       sortable: true,
        //       resizable: false,
        //       filterParams: commonFilterParams,
        //       filter: commonFilter,
        //       suppressMovable: true,
        //       unSortIcon: true,
        //     },
        //     {
        //       headerName: 'Allergen Description',
        //       field: "allergenknown",
        //       sortable: true,
        //       resizable: false,
        //       filterParams: commonFilterParams,
        //       filter: commonFilter,
        //       suppressMovable: true,
        //       unSortIcon: true,
        //     },
        //     {
        //       headerName: 'Fairtrade',
        //       field: "fairtradecertified",
        //       sortable: true,
        //       resizable: false,
        //       filterParams: commonFilterParams,
        //       filter: commonFilter,
        //       suppressMovable: true,
        //       unSortIcon: true,
        //     },
        //   ]
        // },
        {
          headerName: 'Forecast',
          field: "foreCastFile",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('foreCastFile')
          }
        },
        {
          headerName: 'Status',
          field: "status",
          pinned: "right",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('status')
          }
        },
      ];
    } else if (props?.data?.priceMemoType === 'Agri') {
        return [
          {
            headerName: "User Id",
            field: "userId",
            sortable: true,
            resizable: false,
            unSortIcon: true,
          },
          {
            headerName: "User Name",
            field: "userName",
            sortable: true,
            resizable: false,
            unSortIcon: true,
          },
          {
            headerName: "Created Date",
            field: "lastUpdateDate",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            // provide a JS Date for AG Grid sorting/filtering
            valueGetter: (params) => {
              const v = params.data?.lastUpdateDate;
              if (!v || typeof v !== 'string') return null;
              const parts = v.split('-'); // expecting "DD-MM-YYYY"
              if (parts.length !== 3) return null;
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const year = parseInt(parts[2], 10);
              const d = new Date(year, month, day);
              return isNaN(d.getTime()) ? null : d;
            },

            // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
            valueFormatter: (params) => {
              const src = params.data?.lastUpdateDate;
              if (src && typeof src === 'string') return src;
              if (params.value instanceof Date && !isNaN(params.value.getTime())) {
                const d = params.value;
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${dd}-${mm}-${yyyy}`;
              }
              return "-";
            },

            filter: "agDateColumnFilter",
            filterParams: {
              comparator: (filterLocalDateAtMidnight, cellValue, node) => {
                const src = node?.data?.lastUpdateDate ?? cellValue;
                if (!src) return -1;
                // parse "DD-MM-YYYY"
                const parts = typeof src === 'string' ? src.split('-') : null;
                if (!parts || parts.length !== 3) return -1;
                const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
                if (isNaN(d.getTime())) return -1;
                const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                const filterYMD = new Date(
                  filterLocalDateAtMidnight.getFullYear(),
                  filterLocalDateAtMidnight.getMonth(),
                  filterLocalDateAtMidnight.getDate()
                );
                if (cellYMD.getTime() === filterYMD.getTime()) return 0;
                return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
              },
              browserDatePicker: true,
            },

            // sort by parsing the source string from the row data (robust even if other getters exist)
            sortComparator: (valueA, valueB, nodeA, nodeB) => {
              const aStr = nodeA?.data?.lastUpdateDate ?? valueA;
              const bStr = nodeB?.data?.lastUpdateDate ?? valueB;
              const parse = (s) => {
                if (!s || typeof s !== 'string') return null;
                const p = s.split('-');
                if (p.length !== 3) return null;
                const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
                return isNaN(d.getTime()) ? null : d.getTime();
              };
              const ta = parse(aStr);
              const tb = parse(bStr);
              if (ta === null && tb === null) return 0;
              if (ta === null) return -1;
              if (tb === null) return 1;
              return ta - tb;
            },
          },
          {
            headerName: "Created Time",
            field: "lastUpdateTime",
            sortable: true,
            resizable: false,
            unSortIcon: true,
          },
          {
            headerName: 'Resolved Date',
            field: "resolvedDate",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('resolvedDate')
            },
            // provide a JS Date for AG Grid sorting/filtering
            valueGetter: (params) => {
              const v = params.data?.resolvedDate;
              if (!v || typeof v !== 'string') return null;
              const parts = v.split('-'); // expecting "DD-MM-YYYY"
              if (parts.length !== 3) return null;
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const year = parseInt(parts[2], 10);
              const d = new Date(year, month, day);
              return isNaN(d.getTime()) ? null : d;
            },

            // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
            valueFormatter: (params) => {
              const src = params.data?.resolvedDate;
              if (src && typeof src === 'string') return src;
              if (params.value instanceof Date && !isNaN(params.value.getTime())) {
                const d = params.value;
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${dd}-${mm}-${yyyy}`;
              }
              return "-";
            },

            filter: "agDateColumnFilter",
            filterParams: {
              comparator: (filterLocalDateAtMidnight, cellValue, node) => {
                const src = node?.data?.resolvedDate ?? cellValue;
                if (!src) return -1;
                // parse "DD-MM-YYYY"
                const parts = typeof src === 'string' ? src.split('-') : null;
                if (!parts || parts.length !== 3) return -1;
                const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
                if (isNaN(d.getTime())) return -1;
                const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                const filterYMD = new Date(
                  filterLocalDateAtMidnight.getFullYear(),
                  filterLocalDateAtMidnight.getMonth(),
                  filterLocalDateAtMidnight.getDate()
                );
                if (cellYMD.getTime() === filterYMD.getTime()) return 0;
                return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
              },
              browserDatePicker: true,
            },

            // sort by parsing the source string from the row data (robust even if other getters exist)
            sortComparator: (valueA, valueB, nodeA, nodeB) => {
              const aStr = nodeA?.data?.resolvedDate ?? valueA;
              const bStr = nodeB?.data?.resolvedDate ?? valueB;
              const parse = (s) => {
                if (!s || typeof s !== 'string') return null;
                const p = s.split('-');
                if (p.length !== 3) return null;
                const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
                return isNaN(d.getTime()) ? null : d.getTime();
              };
              const ta = parse(aStr);
              const tb = parse(bStr);
              if (ta === null && tb === null) return 0;
              if (ta === null) return -1;
              if (tb === null) return 1;
              return ta - tb;
            },
          },
          {
            headerName: 'Resolved Time',
            field: "resolvedTime",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('resolvedTime')
            }
          },
          {
            headerName: 'Plant Id',
            field: "plantId",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('plantId')
            }
          },
          {
            headerName: 'Plant Name',
            field: "plantName",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('plantName')
            }
          },
          {
            headerName: 'Vendor Number',
            field: "vendorNumber",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('vendorNumber')
            }
          },
          {
            headerName: 'Supplier',
            field: "supplierName",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierName')
            }
          },
          {
            headerName: 'Material Number',
            field: "materialNumber",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('materialNumber')
            }
          },
          {
            headerName: 'Material Description',
            field: "materialDescription",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('materialDescription')
            },
            cellRenderer: (params) => renderExpandableText(params, 50)
          },
          {
            headerName: 'S No.',
            field: "snumber",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('snumber')
            }
          },
          {
            headerName: 'Forecast File',
            field: "foreCastFile",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('foreCastFile')
            }
          },
          {
            headerName: 'Currency',
            field: "currency",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('currency')
            }
          },
          {
            headerName: 'Group',
            field: "groupAgri",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('groupAgri')
            }
          },
          {
            headerName: 'Sub Category',
            field: "subCategory",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('subCategory')
            }
          },
          {
            headerName: 'Brand',
            field: "brand",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('brand')
            }
          },
          {
            headerName: 'Prorement Manager',
            field: "prorementManager",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('prorementManager')
            }
          },
          {
            headerName: 'UOM',
            field: "uom",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('uom')
            }
          },
          {
            headerName: 'Geography',
            field: "geography",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('geography')
            }
          },
          {
            headerName: 'Picasso Approval',
            field: "picassoApproval",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('picassoApproval')
            }
          },
          {
            headerName: 'Material Origin',
            field: "materialOrigin",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('materialOrigin')
            }
          },
          {
            headerName: 'LeadTime',
            field: "leadTime",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('leadTime')
            }
          },
          {
            headerName: 'Ship From Country',
            field: "shipFromCountry",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('shipFromCountry')
            }
          },
          {
            headerName: 'Ship From City',
            field: "shipFromCity",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('shipFromCity')
            }
          },
          {
            headerName: 'Payment Term',
            field: "paymentTerm",
            sortable: true,
            resizable: true,
            unSortIcon: true,
            initialWidth: 180,
            wrapText: true,
            autoHeight: true,
            cellClass: "paymentTerms-cell",
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('paymentTerm')
            }
          },
          {
            headerName: 'Material Cost Per UOM',
            field: "materialCostPerUOM",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('materialCostPerUOM')
            }
          },
          {
            headerName: 'Total Cost Per UOM',
            field: "totalCostPerUOM",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('totalCostPerUOM')
            }
          },
          {
            headerName: 'MOQ Comments',
            field: "moqComments",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            initialWidth: 180,
            wrapText: true,
            autoHeight: true,
            cellClass: "status-cell",
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('moqComments')
            },
            cellRenderer: (params) => renderExpandableText(params, 50)
          },
          {
            headerName: 'RFA Ft Per UOM',
            field: "rfaFtPerUOM",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('rfaFtPerUOM')
            }
          },
          {
            headerName: 'Bid Incoterm',
            field: "bidIncoterm",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('bidIncoterm')
            }
          },
          {
            headerName: 'Open PO',
            field: "openPO",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('openPO')
            }
          },
          {
            headerName: 'Bid Incoterm Location',
            field: "bidIncotermLocation",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('bidIncotermLocation')
            }
          },
          {
            headerName: 'Price Effective Date',
            field: "priceEffectiveDate",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('priceEffectiveDate')
            }
          },
          {
            headerName: 'Price EndDate',
            field: "priceEndDate",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('priceEndDate')
            },
            // provide a JS Date for AG Grid sorting/filtering
            valueGetter: (params) => {
              const v = params.data?.priceEndDate;
              if (!v || typeof v !== 'string') return null;
              const parts = v.split('-'); // expecting "DD-MM-YYYY"
              if (parts.length !== 3) return null;
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const year = parseInt(parts[2], 10);
              const d = new Date(year, month, day);
              return isNaN(d.getTime()) ? null : d;
            },

            // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
            valueFormatter: (params) => {
              const src = params.data?.priceEndDate;
              if (src && typeof src === 'string') return src;
              if (params.value instanceof Date && !isNaN(params.value.getTime())) {
                const d = params.value;
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${dd}-${mm}-${yyyy}`;
              }
              return "-";
            },

            filter: "agDateColumnFilter",
            filterParams: {
              comparator: (filterLocalDateAtMidnight, cellValue, node) => {
                const src = node?.data?.priceEndDate ?? cellValue;
                if (!src) return -1;
                // parse "DD-MM-YYYY"
                const parts = typeof src === 'string' ? src.split('-') : null;
                if (!parts || parts.length !== 3) return -1;
                const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
                if (isNaN(d.getTime())) return -1;
                const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                const filterYMD = new Date(
                  filterLocalDateAtMidnight.getFullYear(),
                  filterLocalDateAtMidnight.getMonth(),
                  filterLocalDateAtMidnight.getDate()
                );
                if (cellYMD.getTime() === filterYMD.getTime()) return 0;
                return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
              },
              browserDatePicker: true,
            },

            // sort by parsing the source string from the row data (robust even if other getters exist)
            sortComparator: (valueA, valueB, nodeA, nodeB) => {
              const aStr = nodeA?.data?.priceEndDate ?? valueA;
              const bStr = nodeB?.data?.priceEndDate ?? valueB;
              const parse = (s) => {
                if (!s || typeof s !== 'string') return null;
                const p = s.split('-');
                if (p.length !== 3) return null;
                const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
                return isNaN(d.getTime()) ? null : d.getTime();
              };
              const ta = parse(aStr);
              const tb = parse(bStr);
              if (ta === null && tb === null) return 0;
              if (ta === null) return -1;
              if (tb === null) return 1;
              return ta - tb;
            },
          },
          {
            headerName: 'Packaging Size',
            field: "packagingSize",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('packagingSize')
            }
          },
          {
            headerName: 'Error Details',
            field: "errorDetails",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            initialWidth: 180,
            wrapText: true,
            autoHeight: true,
            cellClass: "status-cell",
            cellRenderer: (params) => renderExpandableText(params, 50)
          },
          {
            headerName: 'Status',
            field: "status",
            pinned: "right",
            sortable: true,
            resizable: true,
            unSortIcon: true,
            initialWidth: 180,
            wrapText: true,
            autoHeight: true,
            cellClass: "status-cell",
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('status')
            }
          },
        ];
    } else if (props?.data?.priceMemoType === 'Packaging') {
        return [
          {
            headerName: "User Id",
            field: "userId",
            sortable: true,
            resizable: false,
            unSortIcon: true,
          },
          {
            headerName: "User Name",
            field: "userName",
            sortable: true,
            resizable: false,
            unSortIcon: true,
          },
          {
            headerName: "Created Date",
            field: "lastUpdateDate",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            // provide a JS Date for AG Grid sorting/filtering
            valueGetter: (params) => {
              const v = params.data?.lastUpdateDate;
              if (!v || typeof v !== 'string') return null;
              const parts = v.split('-'); // expecting "DD-MM-YYYY"
              if (parts.length !== 3) return null;
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const year = parseInt(parts[2], 10);
              const d = new Date(year, month, day);
              return isNaN(d.getTime()) ? null : d;
            },

            // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
            valueFormatter: (params) => {
              const src = params.data?.lastUpdateDate;
              if (src && typeof src === 'string') return src;
              if (params.value instanceof Date && !isNaN(params.value.getTime())) {
                const d = params.value;
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${dd}-${mm}-${yyyy}`;
              }
              return "-";
            },

            filter: "agDateColumnFilter",
            filterParams: {
              comparator: (filterLocalDateAtMidnight, cellValue, node) => {
                const src = node?.data?.lastUpdateDate ?? cellValue;
                if (!src) return -1;
                // parse "DD-MM-YYYY"
                const parts = typeof src === 'string' ? src.split('-') : null;
                if (!parts || parts.length !== 3) return -1;
                const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
                if (isNaN(d.getTime())) return -1;
                const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                const filterYMD = new Date(
                  filterLocalDateAtMidnight.getFullYear(),
                  filterLocalDateAtMidnight.getMonth(),
                  filterLocalDateAtMidnight.getDate()
                );
                if (cellYMD.getTime() === filterYMD.getTime()) return 0;
                return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
              },
              browserDatePicker: true,
            },

            // sort by parsing the source string from the row data (robust even if other getters exist)
            sortComparator: (valueA, valueB, nodeA, nodeB) => {
              const aStr = nodeA?.data?.lastUpdateDate ?? valueA;
              const bStr = nodeB?.data?.lastUpdateDate ?? valueB;
              const parse = (s) => {
                if (!s || typeof s !== 'string') return null;
                const p = s.split('-');
                if (p.length !== 3) return null;
                const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
                return isNaN(d.getTime()) ? null : d.getTime();
              };
              const ta = parse(aStr);
              const tb = parse(bStr);
              if (ta === null && tb === null) return 0;
              if (ta === null) return -1;
              if (tb === null) return 1;
              return ta - tb;
            },
          },
          {
            headerName: "Created Time",
            field: "lastUpdateTime",
            sortable: true,
            resizable: false,
            unSortIcon: true,
          },
          {
            headerName: 'Resolved Date',
            field: "resolvedDate",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('resolvedDate')
            },
            // provide a JS Date for AG Grid sorting/filtering
            valueGetter: (params) => {
              const v = params.data?.resolvedDate;
              if (!v || typeof v !== 'string') return null;
              const parts = v.split('-'); // expecting "DD-MM-YYYY"
              if (parts.length !== 3) return null;
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const year = parseInt(parts[2], 10);
              const d = new Date(year, month, day);
              return isNaN(d.getTime()) ? null : d;
            },

            // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
            valueFormatter: (params) => {
              const src = params.data?.resolvedDate;
              if (src && typeof src === 'string') return src;
              if (params.value instanceof Date && !isNaN(params.value.getTime())) {
                const d = params.value;
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${dd}-${mm}-${yyyy}`;
              }
              return "-";
            },

            filter: "agDateColumnFilter",
            filterParams: {
              comparator: (filterLocalDateAtMidnight, cellValue, node) => {
                const src = node?.data?.resolvedDate ?? cellValue;
                if (!src) return -1;
                // parse "DD-MM-YYYY"
                const parts = typeof src === 'string' ? src.split('-') : null;
                if (!parts || parts.length !== 3) return -1;
                const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
                if (isNaN(d.getTime())) return -1;
                const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                const filterYMD = new Date(
                  filterLocalDateAtMidnight.getFullYear(),
                  filterLocalDateAtMidnight.getMonth(),
                  filterLocalDateAtMidnight.getDate()
                );
                if (cellYMD.getTime() === filterYMD.getTime()) return 0;
                return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
              },
              browserDatePicker: true,
            },

            // sort by parsing the source string from the row data (robust even if other getters exist)
            sortComparator: (valueA, valueB, nodeA, nodeB) => {
              const aStr = nodeA?.data?.resolvedDate ?? valueA;
              const bStr = nodeB?.data?.resolvedDate ?? valueB;
              const parse = (s) => {
                if (!s || typeof s !== 'string') return null;
                const p = s.split('-');
                if (p.length !== 3) return null;
                const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
                return isNaN(d.getTime()) ? null : d.getTime();
              };
              const ta = parse(aStr);
              const tb = parse(bStr);
              if (ta === null && tb === null) return 0;
              if (ta === null) return -1;
              if (tb === null) return 1;
              return ta - tb;
            },
          },
          {
            headerName: 'Resolved Time',
            field: "resolvedTime",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('resolvedTime')
            }
          },
          {
            headerName: 'Plant Id',
            field: "plantId",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('plantId')
            }
          },
          {
            headerName: 'Plant Name',
            field: "plantName",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('plantName')
            }
          },
          {
            headerName: 'Supplier',
            field: "supplierName",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierName')
            }
          },
          {
            headerName: 'Vendor Number',
            field: "vendorNumber",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('vendorNumber')
            }
          },
          {
            headerName: 'CC Material No',
            field: "ccMaterialNo",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('ccMaterialNo')
            }
          },
          {
            headerName: 'Material Description',
            field: "materialDescription",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            initialWidth: 180,
            wrapText: true,
            autoHeight: true,
            cellClass: "status-cell",
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('materialDescription')
            },
            cellRenderer: (params) => renderExpandableText(params, 50)
          },
          {
            headerName: 'Year',
            field: "yearPckg",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('yearPckg')
            }
          },
          {
            headerName: 'Division',
            field: "division",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('division')
            }
          },
          {
            headerName: 'Forecast File',
            field: "foreCastFile",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('foreCastFile')
            }
          },
          {
            headerName: 'Location Name',
            field: "locationName",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('locationName')
            }
          },
          {
            headerName: 'Manager',
            field: "managerName",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('managerName')
            }
          },
          {
            headerName: 'Last Change',
            field: "lastChange",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('lastChange')
            },
            // provide a JS Date for AG Grid sorting/filtering
            valueGetter: (params) => {
              const v = params.data?.lastChange;
              if (!v || typeof v !== 'string') return null;
              const parts = v.split('-'); // expecting "DD-MM-YYYY"
              if (parts.length !== 3) return null;
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const year = parseInt(parts[2], 10);
              const d = new Date(year, month, day);
              return isNaN(d.getTime()) ? null : d;
            },

            // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
            valueFormatter: (params) => {
              const src = params.data?.lastChange;
              if (src && typeof src === 'string') return src;
              if (params.value instanceof Date && !isNaN(params.value.getTime())) {
                const d = params.value;
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${dd}-${mm}-${yyyy}`;
              }
              return "-";
            },

            filter: "agDateColumnFilter",
            filterParams: {
              comparator: (filterLocalDateAtMidnight, cellValue, node) => {
                const src = node?.data?.lastChange ?? cellValue;
                if (!src) return -1;
                // parse "DD-MM-YYYY"
                const parts = typeof src === 'string' ? src.split('-') : null;
                if (!parts || parts.length !== 3) return -1;
                const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
                if (isNaN(d.getTime())) return -1;
                const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                const filterYMD = new Date(
                  filterLocalDateAtMidnight.getFullYear(),
                  filterLocalDateAtMidnight.getMonth(),
                  filterLocalDateAtMidnight.getDate()
                );
                if (cellYMD.getTime() === filterYMD.getTime()) return 0;
                return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
              },
              browserDatePicker: true,
            },

            // sort by parsing the source string from the row data (robust even if other getters exist)
            sortComparator: (valueA, valueB, nodeA, nodeB) => {
              const aStr = nodeA?.data?.lastChange ?? valueA;
              const bStr = nodeB?.data?.lastChange ?? valueB;
              const parse = (s) => {
                if (!s || typeof s !== 'string') return null;
                const p = s.split('-');
                if (p.length !== 3) return null;
                const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
                return isNaN(d.getTime()) ? null : d.getTime();
              };
              const ta = parse(aStr);
              const tb = parse(bStr);
              if (ta === null && tb === null) return 0;
              if (ta === null) return -1;
              if (tb === null) return 1;
              return ta - tb;
            },
          },
          {
            headerName: 'Named Place',
            field: "namedPlace",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('namedPlace')
            }
          },
          {
            headerName: 'Payment Description',
            field: "paymentDescription",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            initialWidth: 180,
            wrapText: true,
            autoHeight: true,
            cellClass: "status-cell",
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('paymentDescription')
            },
            cellRenderer: (params) => renderExpandableText(params, 50)
          },
          {
            headerName: 'Price',
            field: "price",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('price')
            }
          },
          {
            headerName: 'Supplier Description',
            field: "supplierDescription",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            initialWidth: 180,
            wrapText: true,
            autoHeight: true,
            cellClass: "status-cell",
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierDescription')
            },
            cellRenderer: (params) => renderExpandableText(params, 50)
          },
          {
            headerName: 'Supplier Item No',
            field: "supplierItemNo",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierItemNo')
            }
          },
          {
            headerName: 'Material Type',
            field: "materialType",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('materialType')
            }
          },
          {
            headerName: 'Currency',
            field: "currency",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('currency')
            }
          },
          {
            headerName: 'UOM',
            field: "uom",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('uom')
            }
          },
          {
            headerName: 'Inco Terms',
            field: "incoterms",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('incoterms')
            }
          },
          {
            headerName: 'From (Qty)',
            field: "fromQty",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('fromQty')
            }
          },
          {
            headerName: 'To (Qty)',
            field: "toQty",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('toQty')
            }
          },
          {
            headerName: 'Effective From',
            field: "effectiveStartDate",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('effectiveStartDate')
            },
            // provide a JS Date for AG Grid sorting/filtering
            valueGetter: (params) => {
              const v = params.data?.effectiveStartDate;
              if (!v || typeof v !== 'string') return null;
              const parts = v.split('-'); // expecting "DD-MM-YYYY"
              if (parts.length !== 3) return null;
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const year = parseInt(parts[2], 10);
              const d = new Date(year, month, day);
              return isNaN(d.getTime()) ? null : d;
            },

            // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
            valueFormatter: (params) => {
              const src = params.data?.effectiveStartDate;
              if (src && typeof src === 'string') return src;
              if (params.value instanceof Date && !isNaN(params.value.getTime())) {
                const d = params.value;
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${dd}-${mm}-${yyyy}`;
              }
              return "-";
            },

            filter: "agDateColumnFilter",
            filterParams: {
              comparator: (filterLocalDateAtMidnight, cellValue, node) => {
                const src = node?.data?.effectiveStartDate ?? cellValue;
                if (!src) return -1;
                // parse "DD-MM-YYYY"
                const parts = typeof src === 'string' ? src.split('-') : null;
                if (!parts || parts.length !== 3) return -1;
                const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
                if (isNaN(d.getTime())) return -1;
                const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                const filterYMD = new Date(
                  filterLocalDateAtMidnight.getFullYear(),
                  filterLocalDateAtMidnight.getMonth(),
                  filterLocalDateAtMidnight.getDate()
                );
                if (cellYMD.getTime() === filterYMD.getTime()) return 0;
                return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
              },
              browserDatePicker: true,
            },

            // sort by parsing the source string from the row data (robust even if other getters exist)
            sortComparator: (valueA, valueB, nodeA, nodeB) => {
              const aStr = nodeA?.data?.effectiveStartDate ?? valueA;
              const bStr = nodeB?.data?.effectiveStartDate ?? valueB;
              const parse = (s) => {
                if (!s || typeof s !== 'string') return null;
                const p = s.split('-');
                if (p.length !== 3) return null;
                const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
                return isNaN(d.getTime()) ? null : d.getTime();
              };
              const ta = parse(aStr);
              const tb = parse(bStr);
              if (ta === null && tb === null) return 0;
              if (ta === null) return -1;
              if (tb === null) return 1;
              return ta - tb;
            },
          },
          {
            headerName: 'Effective To',
            field: "effectiveEndDate",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('effectiveEndDate')
            },
            // provide a JS Date for AG Grid sorting/filtering
            valueGetter: (params) => {
              const v = params.data?.effectiveEndDate;
              if (!v || typeof v !== 'string') return null;
              const parts = v.split('-'); // expecting "DD-MM-YYYY"
              if (parts.length !== 3) return null;
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const year = parseInt(parts[2], 10);
              const d = new Date(year, month, day);
              return isNaN(d.getTime()) ? null : d;
            },

            // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
            valueFormatter: (params) => {
              const src = params.data?.effectiveEndDate;
              if (src && typeof src === 'string') return src;
              if (params.value instanceof Date && !isNaN(params.value.getTime())) {
                const d = params.value;
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${dd}-${mm}-${yyyy}`;
              }
              return "-";
            },

            filter: "agDateColumnFilter",
            filterParams: {
              comparator: (filterLocalDateAtMidnight, cellValue, node) => {
                const src = node?.data?.effectiveEndDate ?? cellValue;
                if (!src) return -1;
                // parse "DD-MM-YYYY"
                const parts = typeof src === 'string' ? src.split('-') : null;
                if (!parts || parts.length !== 3) return -1;
                const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
                if (isNaN(d.getTime())) return -1;
                const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                const filterYMD = new Date(
                  filterLocalDateAtMidnight.getFullYear(),
                  filterLocalDateAtMidnight.getMonth(),
                  filterLocalDateAtMidnight.getDate()
                );
                if (cellYMD.getTime() === filterYMD.getTime()) return 0;
                return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
              },
              browserDatePicker: true,
            },

            // sort by parsing the source string from the row data (robust even if other getters exist)
            sortComparator: (valueA, valueB, nodeA, nodeB) => {
              const aStr = nodeA?.data?.effectiveEndDate ?? valueA;
              const bStr = nodeB?.data?.effectiveEndDate ?? valueB;
              const parse = (s) => {
                if (!s || typeof s !== 'string') return null;
                const p = s.split('-');
                if (p.length !== 3) return null;
                const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
                return isNaN(d.getTime()) ? null : d.getTime();
              };
              const ta = parse(aStr);
              const tb = parse(bStr);
              if (ta === null && tb === null) return 0;
              if (ta === null) return -1;
              if (tb === null) return 1;
              return ta - tb;
            },
          },
          {
            headerName: 'Sap Payment Term',
            field: "sapPaymentTerm",
            sortable: true,
            resizable: true,
            unSortIcon: true,
            initialWidth: 180,
            wrapText: true,
            autoHeight: true,
            cellClass: "paymentTerms-cell",
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('sapPaymentTerm')
            }
          },
          {
            headerName: 'Manufacturing Lead Time',
            field: "manufacturingLeadTime",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('manufacturingLeadTime')
            }
          },
          {
            headerName: 'MOQ Comments',
            field: "moqComments",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            initialWidth: 180,
            wrapText: true,
            autoHeight: true,
            cellClass: "status-cell",
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('moqComments')
            },
            cellRenderer: (params) => renderExpandableText(params, 50)
          },
          {
            headerName: 'Error Details',
            field: "errorDetails",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            initialWidth: 180,
            wrapText: true,
            autoHeight: true,
            cellClass: "status-cell",
            cellRenderer: (params) => renderExpandableText(params, 50)
          },
          {
            headerName: 'Open PO',
            field: "openPO",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('openPO')
            }
          },
          {
            headerName: 'Status',
            field: "status",
            pinned: "right",
            sortable: true,
            resizable: true,
            unSortIcon: true,
            initialWidth: 180,
            wrapText: true,
            autoHeight: true,
            cellClass: "status-cell",
            cellClassRules: {
              'bold-cell': (params) => params.data.changedFieldNames?.includes('status')
            }
          },
        ];
    } else if (props?.data?.priceMemoType === 'CEPG') {
      return [
        {
          headerName: "User Id",
          field: "userId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: "User Name",
          field: "userName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Plant Id',
          field: "plant",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Plant Name',
          field: "plantName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Material Number',
          field: "material",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50)
        },
        {
          headerName: 'Material Description',
          field: "description",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50)
        },
        {
          headerName: 'Package Size',
          field: "packSize",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'S-Code',
          field: "scode",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Agreement Number',
          field: "agreementNo",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Year',
          field: "year",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Last Updated Date',
          field: "lastUpdateDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          // provide a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.lastUpdateDate;
            if (!v || typeof v !== 'string') return null;
            const parts = v.split('-'); // expecting "DD-MM-YYYY"
            if (parts.length !== 3) return null;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
          },

          // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
          valueFormatter: (params) => {
            const src = params.data?.lastUpdateDate;
            if (src && typeof src === 'string') return src;
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          filterParams: {
            comparator: (filterLocalDateAtMidnight, cellValue, node) => {
              const src = node?.data?.lastUpdateDate ?? cellValue;
              if (!src) return -1;
              // parse "DD-MM-YYYY"
              const parts = typeof src === 'string' ? src.split('-') : null;
              if (!parts || parts.length !== 3) return -1;
              const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
              if (isNaN(d.getTime())) return -1;
              const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
              const filterYMD = new Date(
                filterLocalDateAtMidnight.getFullYear(),
                filterLocalDateAtMidnight.getMonth(),
                filterLocalDateAtMidnight.getDate()
              );
              if (cellYMD.getTime() === filterYMD.getTime()) return 0;
              return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
            },
            browserDatePicker: true,
          },

          // sort by parsing the source string from the row data (robust even if other getters exist)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.lastUpdateDate ?? valueA;
            const bStr = nodeB?.data?.lastUpdateDate ?? valueB;
            const parse = (s) => {
              if (!s || typeof s !== 'string') return null;
              const p = s.split('-');
              if (p.length !== 3) return null;
              const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
              return isNaN(d.getTime()) ? null : d.getTime();
            };
            const ta = parse(aStr);
            const tb = parse(bStr);
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'Last Updated Time',
          field: "lastUpdateTime",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Division',
          field: "division",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Forecast File',
          field: "foreCastFile",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Manager',
          field: "manager",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Gmp Lead',
          field: "gmpLead",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Change Date',
          field: "changeDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          // return a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.changeDate;
            if (!v) return null;
            const s = typeof v === 'string' ? v.replace(/\.00$/, '') : v;
            const d = new Date(s);
            return isNaN(d.getTime()) ? null : d;
          },

          // display as DD-MM-YYYY (prefer original string when available)
          valueFormatter: (params) => {
            const src = params.data?.changeDate;
            if (src && typeof src === 'string') {
              const s = src.replace(/\.00$/, '');
              const d = new Date(s);
              if (!isNaN(d.getTime())) {
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${dd}-${mm}-${yyyy}`;
              }
              return src;
            }
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          filterParams: {
            comparator: (filterLocalDateAtMidnight, cellValue, node) => {
              const src = node?.data?.changeDate ?? cellValue;
              if (!src) return -1;
              const s = typeof src === 'string' ? src.replace(/\.00$/, '') : src;
              const d = new Date(s);
              if (isNaN(d.getTime())) return -1;
              const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
              const filterYMD = new Date(
                filterLocalDateAtMidnight.getFullYear(),
                filterLocalDateAtMidnight.getMonth(),
                filterLocalDateAtMidnight.getDate()
              );
              if (cellYMD.getTime() === filterYMD.getTime()) return 0;
              return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
            },
            browserDatePicker: true,
          },

          // ensure sort parses the original string (works even when valueGetter removed elsewhere)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.changeDate ?? valueA;
            const bStr = nodeB?.data?.changeDate ?? valueB;
            const aDate = aStr ? new Date((typeof aStr === 'string' ? aStr.replace(/\.00$/, '') : aStr)) : null;
            const bDate = bStr ? new Date((typeof bStr === 'string' ? bStr.replace(/\.00$/, '') : bStr)) : null;
            const ta = aDate && !isNaN(aDate.getTime()) ? aDate.getTime() : null;
            const tb = bDate && !isNaN(bDate.getTime()) ? bDate.getTime() : null;
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'Named Place',
          field: "namedPlace",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Price',
          field: "price",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Supplier Name',
          field: "supplierName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50)
        },
        {
          headerName: 'Supplier Item No',
          field: "supplierNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Material Type',
          field: "material",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Currency',
          field: "currency",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'UOM',
          field: "uom",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Inco Terms',
          field: "incoTerms",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'From (Qty)',
          field: "fromQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'To (Qty)',
          field: "toQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Effective From',
          field: "effectiveFrom",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          // provide a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.effectiveFrom;
            if (!v || typeof v !== 'string') return null;
            const parts = v.split('-'); // expecting "DD-MM-YYYY"
            if (parts.length !== 3) return null;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
          },

          // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
          valueFormatter: (params) => {
            const src = params.data?.effectiveFrom;
            if (src && typeof src === 'string') return src;
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          filterParams: {
            comparator: (filterLocalDateAtMidnight, cellValue, node) => {
              const src = node?.data?.effectiveFrom ?? cellValue;
              if (!src) return -1;
              // parse "DD-MM-YYYY"
              const parts = typeof src === 'string' ? src.split('-') : null;
              if (!parts || parts.length !== 3) return -1;
              const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
              if (isNaN(d.getTime())) return -1;
              const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
              const filterYMD = new Date(
                filterLocalDateAtMidnight.getFullYear(),
                filterLocalDateAtMidnight.getMonth(),
                filterLocalDateAtMidnight.getDate()
              );
              if (cellYMD.getTime() === filterYMD.getTime()) return 0;
              return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
            },
            browserDatePicker: true,
          },

          // sort by parsing the source string from the row data (robust even if other getters exist)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.effectiveFrom ?? valueA;
            const bStr = nodeB?.data?.effectiveFrom ?? valueB;
            const parse = (s) => {
              if (!s || typeof s !== 'string') return null;
              const p = s.split('-');
              if (p.length !== 3) return null;
              const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
              return isNaN(d.getTime()) ? null : d.getTime();
            };
            const ta = parse(aStr);
            const tb = parse(bStr);
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'Effective To',
          field: "effectiveTo",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          // provide a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.effectiveTo;
            if (!v || typeof v !== 'string') return null;
            const parts = v.split('-'); // expecting "DD-MM-YYYY"
            if (parts.length !== 3) return null;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
          },

          // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
          valueFormatter: (params) => {
            const src = params.data?.effectiveTo;
            if (src && typeof src === 'string') return src;
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          filterParams: {
            comparator: (filterLocalDateAtMidnight, cellValue, node) => {
              const src = node?.data?.effectiveTo ?? cellValue;
              if (!src) return -1;
              // parse "DD-MM-YYYY"
              const parts = typeof src === 'string' ? src.split('-') : null;
              if (!parts || parts.length !== 3) return -1;
              const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
              if (isNaN(d.getTime())) return -1;
              const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
              const filterYMD = new Date(
                filterLocalDateAtMidnight.getFullYear(),
                filterLocalDateAtMidnight.getMonth(),
                filterLocalDateAtMidnight.getDate()
              );
              if (cellYMD.getTime() === filterYMD.getTime()) return 0;
              return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
            },
            browserDatePicker: true,
          },

          // sort by parsing the source string from the row data (robust even if other getters exist)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.effectiveTo ?? valueA;
            const bStr = nodeB?.data?.effectiveTo ?? valueB;
            const parse = (s) => {
              if (!s || typeof s !== 'string') return null;
              const p = s.split('-');
              if (p.length !== 3) return null;
              const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
              return isNaN(d.getTime()) ? null : d.getTime();
            };
            const ta = parse(aStr);
            const tb = parse(bStr);
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'PIN',
          field: "pin",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Alocation Quantity',
          field: "allocationQtyKg",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Allocation Notes',
          field: "allocationNotes",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Payment Term',
          field: "paymentTerm",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
        },
        {
          headerName: 'Picasso Facility',
          field: "picassoFacility",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
        },
        {
          headerName: 'Manufacturing Lead Time',
          field: "manufacturingLeadTime",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'MOQ Comments',
          field: "moqComments",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50)
        },
        {
          headerName: 'Error Details',
          field: "errorDetails",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50)
        },
        {
          headerName: 'Open PO',
          field: "openPO",
          sortable: true,
          resizable: false,
          unSortIcon: true,
        },
        {
          headerName: 'Status',
          field: "status",
          pinned: "right",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
        }
      ];
    }
    }, [props, props.data])

  const selectionColumnDef = useMemo(() => ({
    pinned: "left",
  }), []);

  const gridApiRef = useRef(null); // Reference to the grid API

  const onColumnChange = e => {
    const col = e.target.value;
    setSelectedColumn(col);
    if (col && gridApiRef.current) {
      gridApiRef.current.ensureColumnVisible(col);
    }
  };

  const onGridReady = (params) => {
    gridApiRef.current = params.api; // Store the grid API instance
    setIsGridReady(true);
    if (columnState) {
      params.columnApi.applyColumnState({ state: columnState, applyOrder: true });
    }
  };

  useEffect(() => {
    if (isGridReady && gridApiRef?.current) {
      const savedFilterModel = localStorage.getItem("priceMemoReportFilterModel");
      if (savedFilterModel) {
        gridApiRef.current.setFilterModel(JSON.parse(savedFilterModel));
        setDisableFilterBtn(true);
      }
    }
  }, [isGridReady]);

  const clearAllFilters = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null); // Clear all filters
      localStorage.removeItem("priceMemoReportFilterModel");
      setDisableFilterBtn(true);
    }
  };

  const onFilterChanged = (params) => {
    if (gridApiRef.current) {
      const filterModel = gridApiRef.current.getFilterModel();
      setDisableFilterBtn(Object.keys(filterModel).length === 0); // Enable button if filters exist
      setFilteredTotalColumn(Object.keys(filterModel).length);

      // Get the filtered and total row counts
      const filteredRowCount = params.api.getDisplayedRowCount();
      localStorage.setItem("priceMemoReportFilterModel", JSON.stringify(filterModel));
      setFilteredTotalItems(filteredRowCount);
    }
  };

  const onColumnMoved = useCallback((params) => {
    if (params && params.columnApi && typeof params.columnApi.getColumnState === "function") {
      setColumnState(params.columnApi.getColumnState());
    }
  }, []);

  const gridOptions = {
    getRowId: (params) => {
      return `${params?.data?.runOutputSeqId}`;
    },
    overlayLoadingTemplate: `<span class="ag--overlay-loading-center" >Please wait while data is loading</span>`,
    overlayNoRowsTemplate: `<span class="ag--overlay-no-rows-center" >No Data Available</span>`,
    defaultColDef: {
      sortable: true,
      filter: 'agMultiColumnFilter',
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.5,
        display: "flex",
        alignItems: "center",
      },
      valueGetter: (params) => {
        if (!params.data) return "-";

        const field = params.colDef.field;
        const value = params.data[field];

        return value === null || value === undefined || value === ""
          ? "-"
          : value;
      },
    },
    onColumnMoved,
    onFilterChanged,
  };

  const getContextMenuItems = (params) => {
    const defaultItems = params.defaultItems || [];

    // Remove the 'export' option
    return defaultItems.filter(item => item !== 'export');
  };

  return (
    <>
      <div style={{display: 'flex', marginBottom: '20px', justifyContent: 'space-between', alignItems: 'center'}}>
        <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
          <InputLabel>Jump to Column</InputLabel>
          <Select
            id="col-select"
            value={selectedColumn}
            label="Jump to Column"
            onChange={onColumnChange}
          >
            <MenuItem value="">Select Column</MenuItem>
            {columnData?.map(col => (
              <MenuItem key={col?.field} value={col?.field}>
                {col?.headerName || col?.field}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ButtonComponent
          maxWidth="150px"
          label="Clear All Filters"
          bgColor="black"
          color="white"
          disabled={disableFilterBtn}
          onClick={clearAllFilters}
        />
      </div>
      <DataTablePagination
        columns={columnData}
        rows={props?.data?.priceMemoReport || []}
        onPageSizeChanged={props.onPageSizeChanged}
        onRowSelected={props.onRowSelected}
        rowSelection={props.rowSelection}
        selectionColumnDef={selectionColumnDef}
        goToPage={props.goToPage}
        pageSize={props.pageSize}
        currentPage={props.currentPage}
        totalPages={props.totalPages}
        totalPageItems={filteredTotalItems}
        totalItemCurrentPage={props.totalItemCurrentPage}
        gridOptions={gridOptions}
        onGridReady={onGridReady}
        filteredTotalColumn={filteredTotalColumn}
        postFilterChanged={handlePostFilterChanged}
        onSelectionChanged={props.onSelectionChanged}
        enableCharts={true}
        cellSelection={true}
        getContextMenuItems={getContextMenuItems}
      />
    </>
  );
};

export default PriceMemoData;
